/**
 * Invoices — the generic rail for impromptu charges between a provider and a
 * client they already work with (extra trip, rubble removal, design revision…).
 * Provider sends an itemized invoice; client accepts & pays through Eqaya
 * (held in escrow) and confirms — or it auto-releases after 72 hours unless
 * disputed. 3% platform commission, same as milestones and call-out fees.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Container, Typography, Box, Paper, Button, Chip, Alert, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  IconButton, Divider, Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import { Add, Delete, ReceiptLong } from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { brand } from '../theme/colors';

const PROVIDER_ROLES = ['contractor', 'verifier', 'professional_verifier'];
const PLATFORM_FEE = 0.03;

const STATUS_CHIP = {
  sent: { label: 'Awaiting client', color: 'warning' },
  paid_held: { label: 'Paid — held in escrow', color: 'info' },
  released: { label: 'Released', color: 'success' },
  declined: { label: 'Declined', color: 'error' },
  cancelled: { label: 'Cancelled', color: 'default' },
  disputed: { label: 'Disputed — under review', color: 'error' },
};

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

export default function InvoicesPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isProvider = PROVIDER_ROLES.includes(user?.role);

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [busyId, setBusyId] = useState(null);

  // Create dialog state (providers)
  const [createOpen, setCreateOpen] = useState(false);
  const [contexts, setContexts] = useState([]);
  const [contextKey, setContextKey] = useState('');
  const [title, setTitle] = useState('');
  const [note, setNote] = useState('');
  const [lines, setLines] = useState([{ description: '', quantity: '1', unitPrice: '' }]);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/invoices');
      if (res.data.success) setInvoices(res.data.data || []);
    } catch {
      setError('Failed to load invoices');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Stripe redirect recovery: confirm the payment even if the webhook is slow.
  useEffect(() => {
    const invId = searchParams.get('invoice');
    const paySession = searchParams.get('pay_session');
    if (!invId || !paySession) return;
    api.post(`/invoices/${invId}/confirm-payment`, { sessionId: paySession })
      .then(() => { setInfo('Payment confirmed — the amount is held in escrow.'); load(); })
      .catch(() => { /* webhook will settle it */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const openCreate = async () => {
    setCreateOpen(true);
    try {
      const res = await api.get('/invoices/contexts');
      setContexts(res.data.data || []);
    } catch { setContexts([]); }
  };

  const lineTotal = (l) => (parseFloat(l.quantity) || 0) * (parseFloat(l.unitPrice) || 0);
  const total = lines.reduce((s, l) => s + lineTotal(l), 0);

  const handleCreate = async () => {
    const ctx = contexts.find((c) => `${c.kind}:${c.id}` === contextKey);
    if (!ctx) { setError('Choose which project or quote request this invoice relates to'); return; }
    setCreating(true);
    setError('');
    try {
      const res = await api.post('/invoices', {
        clientId: ctx.clientId,
        projectId: ctx.kind === 'project' ? ctx.id : undefined,
        quoteRequestId: ctx.kind === 'quote_request' ? ctx.id : undefined,
        title,
        note: note || undefined,
        lineItems: lines
          .filter((l) => l.description.trim() && parseFloat(l.unitPrice) >= 0)
          .map((l) => ({ description: l.description.trim(), quantity: parseFloat(l.quantity) || 1, unitPrice: parseFloat(l.unitPrice) || 0 })),
      });
      if (res.data.success) {
        setCreateOpen(false);
        setTitle(''); setNote(''); setLines([{ description: '', quantity: '1', unitPrice: '' }]); setContextKey('');
        setInfo('Invoice sent — the client has been notified.');
        load();
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Failed to send invoice');
    }
    setCreating(false);
  };

  const act = async (inv, action, body = {}) => {
    setBusyId(inv.id);
    setError('');
    try {
      if (action === 'pay') {
        const res = await api.post(`/invoices/${inv.id}/checkout`);
        const url = res.data?.data?.checkoutUrl;
        if (url) { window.location.href = url; return; }
        setError('Could not start the payment. Please try again.');
      } else {
        await api.post(`/invoices/${inv.id}/${action}`, body);
        load();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Action failed');
    }
    setBusyId(null);
  };

  const declineWithReason = (inv) => {
    const reason = window.prompt('Why are you declining this invoice? The provider will see your reason.');
    if (reason && reason.trim().length >= 3) act(inv, 'decline', { reason: reason.trim() });
  };
  const disputeWithReason = (inv) => {
    const reason = window.prompt('Describe the problem. The payment stays held and Eqaya support will review.');
    if (reason && reason.trim().length >= 10) act(inv, 'dispute', { reason: reason.trim() });
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Invoices</Typography>
        {isProvider && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ background: brand.gradient }}>
            New Invoice
          </Button>
        )}
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        {isProvider
          ? 'Bill a client for agreed extras that are not part of a funded stage plan — the money is paid through Eqaya, held in the project wallet, and released to your earnings when they confirm (or automatically after 72 hours). Money already in a project wallet is released through the stage completion report, not an invoice.'
          : 'Invoices are for extras your contractors and verifiers charge outside a funded stage plan. You approve every charge: pay securely through Eqaya, and the money is only released once you confirm — or automatically after 72 hours unless you raise a problem. Money you have already put in a project wallet is released when you approve the contractor\'s stage completion report on the project page — no invoice needed.'}
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {info && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>{info}</Alert>}

      {loading ? (
        <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>
      ) : invoices.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
          <ReceiptLong sx={{ fontSize: 44, color: 'text.disabled' }} />
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            No invoices yet.{isProvider ? ' Send one with "New Invoice".' : ''}
          </Typography>
        </Paper>
      ) : (
        invoices.map((inv) => {
          const mineToPay = String(inv.client_id) === String(user?.id);
          const chip = STATUS_CHIP[inv.status] || { label: inv.status, color: 'default' };
          const items = Array.isArray(inv.line_items) ? inv.line_items : [];
          return (
            <Paper key={inv.id} variant="outlined" sx={{ p: 2.5, mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{inv.title}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {inv.invoice_number} · {mineToPay ? `From ${inv.issuer_name}` : `To ${inv.client_name}`} · {new Date(inv.created_at).toLocaleDateString()}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{money(inv.total)}</Typography>
                  <Chip size="small" label={chip.label} color={chip.color} />
                </Box>
              </Box>

              {items.length > 0 && (
                <Table size="small" sx={{ mt: 1.5 }}>
                  <TableBody>
                    {items.map((li, i) => (
                      <TableRow key={i}>
                        <TableCell sx={{ border: 0, py: 0.25, pl: 0 }}>{li.description}</TableCell>
                        <TableCell sx={{ border: 0, py: 0.25 }} align="right">{li.quantity} × {money(li.unitPrice)}</TableCell>
                        <TableCell sx={{ border: 0, py: 0.25, pr: 0 }} align="right">{money(li.amount)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              {inv.note && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>"{inv.note}"</Typography>
              )}
              {inv.status === 'declined' && inv.decline_reason && (
                <Alert severity="warning" sx={{ mt: 1 }}>Declined: "{inv.decline_reason}"</Alert>
              )}
              {inv.status === 'disputed' && inv.dispute_reason && (
                <Alert severity="error" sx={{ mt: 1 }}>Disputed: "{inv.dispute_reason}" — release paused, Eqaya support will review.</Alert>
              )}
              {inv.status === 'paid_held' && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Held in escrow.{' '}
                  {mineToPay
                    ? 'Confirm to release it now, or it releases automatically 72 hours after payment unless you report a problem.'
                    : 'Released to your earnings when the client confirms, or automatically after 72 hours.'}
                </Alert>
              )}

              <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                {mineToPay && inv.status === 'sent' && (
                  <>
                    <Button variant="contained" size="small" disabled={busyId === inv.id} onClick={() => act(inv, 'pay')} sx={{ background: brand.gradient }}>
                      {busyId === inv.id ? 'Opening secure checkout…' : `Accept & Pay ${money(inv.total)}`}
                    </Button>
                    <Button size="small" color="error" disabled={busyId === inv.id} onClick={() => declineWithReason(inv)}>Decline</Button>
                  </>
                )}
                {mineToPay && inv.status === 'paid_held' && (
                  <>
                    <Button variant="contained" size="small" color="success" disabled={busyId === inv.id} onClick={() => act(inv, 'confirm-received')}>
                      Confirm — release payment
                    </Button>
                    <Button size="small" color="error" disabled={busyId === inv.id} onClick={() => disputeWithReason(inv)}>Report a problem</Button>
                  </>
                )}
                {!mineToPay && inv.status === 'sent' && (
                  <Button size="small" color="error" disabled={busyId === inv.id} onClick={() => act(inv, 'cancel')}>Cancel invoice</Button>
                )}
              </Box>
            </Paper>
          );
        })
      )}

      {/* ── Create dialog (providers) ── */}
      <Dialog open={createOpen} onClose={() => !creating && setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Invoice</DialogTitle>
        <DialogContent>
          <TextField
            select fullWidth label="Related to" value={contextKey}
            onChange={(e) => setContextKey(e.target.value)}
            helperText="You can only invoice clients you share an active project or quote request with"
            sx={{ mt: 1, mb: 2 }}
          >
            {contexts.length === 0 && <MenuItem disabled value="">No active projects or quote requests</MenuItem>}
            {contexts.map((c) => (
              <MenuItem key={`${c.kind}:${c.id}`} value={`${c.kind}:${c.id}`}>
                {c.clientName} — {c.label} {c.kind === 'project' ? '(project)' : '(quote request)'}
              </MenuItem>
            ))}
          </TextField>
          <TextField fullWidth label="What is this invoice for?" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Second site trip + waste disposal" sx={{ mb: 2 }} />
          {lines.map((l, i) => (
            <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
              <TextField size="small" label="Item" value={l.description} onChange={(e) => setLines((p) => p.map((x, j) => j === i ? { ...x, description: e.target.value } : x))} sx={{ flex: 2 }} />
              <TextField size="small" label="Qty" type="number" value={l.quantity} onChange={(e) => setLines((p) => p.map((x, j) => j === i ? { ...x, quantity: e.target.value } : x))} sx={{ width: 76 }} />
              <TextField size="small" label="Price" type="number" value={l.unitPrice} onChange={(e) => setLines((p) => p.map((x, j) => j === i ? { ...x, unitPrice: e.target.value } : x))} sx={{ width: 100 }} />
              <IconButton size="small" disabled={lines.length === 1} onClick={() => setLines((p) => p.filter((_, j) => j !== i))}><Delete fontSize="small" /></IconButton>
            </Box>
          ))}
          <Button size="small" startIcon={<Add />} disabled={lines.length >= 20} onClick={() => setLines((p) => [...p, { description: '', quantity: '1', unitPrice: '' }])}>
            Add line
          </Button>
          <TextField fullWidth multiline rows={2} label="Note to the client (why this charge)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ mt: 2 }} />
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="subtitle2">Total: {money(total)}</Typography>
            <Typography variant="caption" color="text.secondary">
              You receive {money(total * (1 - PLATFORM_FEE))} after the 3% platform fee
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={creating}>Cancel</Button>
          <Button
            variant="contained"
            disabled={creating || !contextKey || title.trim().length < 3 || total < 1}
            onClick={handleCreate}
            sx={{ background: brand.gradient }}
          >
            {creating ? 'Sending…' : 'Send Invoice'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
